import { useThemeMode } from '../../design-system/theme';
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import ChallengeClarityPaint from '../challenges/ChallengeClarityPaint';
import { CHALLENGE_PAGE_EXPERIMENT } from '../challenges/challengePageExperiment';
import { BOOTCAMP_PAGE_EXPERIMENT } from '../courses/coursePageExperiment';
import './EducationDesignExperiment.css';
import './EducationControlRoom.css';
import './EducationControlRoomRefinement.css';
import '../courses/CoursePageControlRoom.css';
import '../courses/CoursePageClarity.css';
import '../challenges/ChallengePageClarity.css';

// form_first is the C arm of the challenge ABC test (Rob, 2026-09-23). It is
// built on the control page, so it is not a workbench design.
export const EDUCATION_DESIGN_VARIANTS = [
  'control',
  'control_room',
  'clarity',
  'form_first',
] as const;
export type EducationDesignVariant = (typeof EDUCATION_DESIGN_VARIANTS)[number];
export type EducationTheme = 'light' | 'dark';
/** Live bootcamp experiment paints Clarity. Control Room is challenge-only. */
export const BOOTCAMP_DESIGN_VARIANTS: readonly EducationDesignVariant[] = ['control', 'clarity'];
const FALLBACK_DESIGN_VARIANTS: readonly EducationDesignVariant[] = ['control', 'control_room'];

type PostHogVariant = 'control' | 'test' | 'clarity' | 'form_first';
const FALLBACK_POSTHOG_VARIANTS: readonly PostHogVariant[] = ['control', 'test'];

const EducationDesignContext = createContext<EducationDesignVariant>('control');

export const useEducationDesignVariant = (): EducationDesignVariant =>
  useContext(EducationDesignContext);

/** Review-route preview. Does not evaluate or enroll a PostHog experiment. */
export function EducationDesignPreview({
  variant,
  children,
}: {
  variant: EducationDesignVariant;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <EducationDesignContext.Provider value={variant}>{children}</EducationDesignContext.Provider>
  );
}

const assignmentKeyFor = (featureFlag: string): string => `${featureFlag}-assignment`;
const CHALLENGE_DESIGN_SESSION_ASSIGNMENT_KEY = assignmentKeyFor(
  CHALLENGE_PAGE_EXPERIMENT.featureFlag,
);
const BOOTCAMP_DESIGN_SESSION_ASSIGNMENT_KEY = assignmentKeyFor(
  BOOTCAMP_PAGE_EXPERIMENT.featureFlag,
);

type EducationDesignExperimentProps = {
  children: React.ReactNode;
};

type EducationDesignABTestResult = {
  variant: string | boolean | null;
  isLoading: boolean;
  isExperimentActive: boolean;
};

export const normalizeEducationDesignVariant = (
  value: string | boolean | null,
): EducationDesignVariant | null => {
  if (value === 'control') return 'control';
  if (value === 'control_room' || value === 'test') return 'control_room';
  if (value === 'clarity' || value === 'test_clarity') return 'clarity';
  if (value === 'form_first') return 'form_first';
  return null;
};

export const isEducationWorkbenchVariant = (variant: EducationDesignVariant): boolean =>
  variant === 'control_room' || variant === 'clarity';

const toPostHogVariant = (
  variant: EducationDesignVariant,
  posthogVariants: readonly PostHogVariant[] = FALLBACK_POSTHOG_VARIANTS,
): PostHogVariant => {
  if (variant === 'control') return 'control';
  // The C arm keeps its own flag value so an exposure never reports it as B.
  if (variant === 'form_first') return 'form_first';
  if (variant === 'clarity' && posthogVariants.includes('clarity')) return 'clarity';
  return 'test';
};

/**
 * Map a PostHog flag value or stored assignment onto a design variant.
 * Challenge: `test` → Control Room. Bootcamp: `test` → Clarity (never Control Room).
 */
export const resolveEducationDesignVariant = (
  value: string | boolean | null,
  allowed: readonly EducationDesignVariant[],
): EducationDesignVariant | null => {
  const bootcampClarityOnly = allowed.includes('clarity') && !allowed.includes('control_room');
  if (value === 'test' && bootcampClarityOnly) {
    return 'clarity';
  }
  const normalized = normalizeEducationDesignVariant(value);
  if (!normalized) return null;
  if (allowed.includes(normalized)) return normalized;
  // Sticky bootcamp sessions from the retired Control Room treatment.
  if (normalized === 'control_room' && bootcampClarityOnly) {
    return 'clarity';
  }
  return null;
};

// Rob, 2026-09-11: the registration page lives at /challenges only. Old
// per-run URLs redirect there before anything renders, and the welcome and
// call-confirmed steps are never an experiment surface.
export const isChallengeExperimentPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === '/challenges';

export const isBootcampExperimentPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '') === '/courses/ai-trading-bootcamp';

type EducationExperimentTarget = {
  experiment: typeof CHALLENGE_PAGE_EXPERIMENT | typeof BOOTCAMP_PAGE_EXPERIMENT;
  sessionKey: string;
  surface: 'challenge_landing_page' | 'ai_trading_bootcamp_page';
  pathProperty: 'challenge_path' | 'course_path';
  previewSource: string;
  sessionSource: string;
  posthogVariants: readonly PostHogVariant[];
  designVariants: readonly EducationDesignVariant[];
};

const resolveExperimentTarget = (pathname: string): EducationExperimentTarget | null => {
  if (isChallengeExperimentPath(pathname)) {
    return {
      experiment: CHALLENGE_PAGE_EXPERIMENT,
      sessionKey: CHALLENGE_DESIGN_SESSION_ASSIGNMENT_KEY,
      surface: 'challenge_landing_page',
      pathProperty: 'challenge_path',
      previewSource: 'challenge_design_preview_override',
      sessionSource: 'challenge_design_session_assignment',
      posthogVariants: CHALLENGE_PAGE_EXPERIMENT.posthogVariants,
      designVariants: EDUCATION_DESIGN_VARIANTS,
    };
  }

  if (isBootcampExperimentPath(pathname)) {
    return {
      experiment: BOOTCAMP_PAGE_EXPERIMENT,
      sessionKey: BOOTCAMP_DESIGN_SESSION_ASSIGNMENT_KEY,
      surface: 'ai_trading_bootcamp_page',
      pathProperty: 'course_path',
      previewSource: 'bootcamp_design_preview_override',
      sessionSource: 'bootcamp_design_session_assignment',
      posthogVariants: BOOTCAMP_PAGE_EXPERIMENT.posthogVariants,
      designVariants: BOOTCAMP_DESIGN_VARIANTS,
    };
  }

  return null;
};

export const useEducationTheme = (): EducationTheme => {
  const [theme] = useThemeMode();
  return theme;
};

export const preserveEducationPreviewParams = (path: string, search: string): string => {
  const sourceParams = new URLSearchParams(search);
  const design = normalizeEducationDesignVariant(sourceParams.get('educationDesign'));
  if (!design) return path;

  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = pathWithoutHash.indexOf('?');
  const pathname = queryIndex >= 0 ? pathWithoutHash.slice(0, queryIndex) : pathWithoutHash;
  const targetParams = new URLSearchParams(
    queryIndex >= 0 ? pathWithoutHash.slice(queryIndex + 1) : '',
  );

  if (design) targetParams.set('educationDesign', design);
  targetParams.delete('educationTheme');

  const query = targetParams.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
};

const readSessionAssignment = (sessionKey: string): EducationDesignVariant | null => {
  try {
    return normalizeEducationDesignVariant(window.sessionStorage.getItem(sessionKey));
  } catch {
    return null;
  }
};

const writeSessionAssignment = (sessionKey: string, variant: EducationDesignVariant): void => {
  try {
    window.sessionStorage.setItem(sessionKey, variant);
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
};

/**
 * Two independent route-scoped presentation experiments. Existing wrappers
 * around other Education routes stay inert: no flag evaluation, exposure,
 * treatment class, or cross-route assignment occurs outside approved pages.
 */
export default function EducationDesignExperiment({
  children,
}: EducationDesignExperimentProps): React.ReactElement {
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const target = useMemo(() => resolveExperimentTarget(location.pathname), [location.pathname]);
  const experiment = target?.experiment || CHALLENGE_PAGE_EXPERIMENT;
  const isTargetPath = Boolean(target);
  const featureFlagKey = experiment.featureFlag;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const resolvedTheme = useEducationTheme();
  const allowedDesignVariants = target?.designVariants || FALLBACK_DESIGN_VARIANTS;
  const previewOverride =
    isTargetPath && isPreviewExperimentOverrideHost()
      ? resolveEducationDesignVariant(searchParams.get('educationDesign'), allowedDesignVariants)
      : null;
  const sessionAssignment = useMemo(
    () =>
      target && !previewOverride
        ? resolveEducationDesignVariant(
            readSessionAssignment(target.sessionKey),
            allowedDesignVariants,
          )
        : null,
    [allowedDesignVariants, previewOverride, target],
  );
  const forcedDesign = !target ? 'control' : previewOverride || sessionAssignment;
  const posthogVariants = target?.posthogVariants || FALLBACK_POSTHOG_VARIANTS;
  const forcedVariant = forcedDesign ? toPostHogVariant(forcedDesign, posthogVariants) : null;

  const trackingData = useMemo(() => {
    const commonData = {
      page: location.pathname,
      surface: target?.surface || 'education_non_experiment',
      education_theme: resolvedTheme,
      education_theme_source: 'preference',
      validVariants: target?.posthogVariants || FALLBACK_POSTHOG_VARIANTS,
      skipFeatureFlagEvaluation: Boolean(forcedVariant),
      forcedVariant: forcedVariant || undefined,
      forcedExposureValue: sessionAssignment
        ? toPostHogVariant(sessionAssignment, posthogVariants)
        : undefined,
      trackForcedExposure: Boolean(target && sessionAssignment),
      exposure_source: previewOverride
        ? target?.previewSource
        : sessionAssignment
          ? target?.sessionSource
          : undefined,
    };
    if (target?.pathProperty === 'challenge_path') {
      return { ...commonData, challenge_path: location.pathname };
    }
    if (target?.pathProperty === 'course_path') {
      return { ...commonData, course_path: location.pathname };
    }
    return commonData;
  }, [
    forcedVariant,
    location.pathname,
    posthogVariants,
    previewOverride,
    resolvedTheme,
    sessionAssignment,
    target,
  ]);

  const abTestResult = useABTest(experiment.name, featureFlagKey, 'control', trackingData);
  // SAFETY: useABTest owns this stable result contract across every caller.
  const { variant, isLoading, isExperimentActive } = abTestResult as EducationDesignABTestResult;

  const assignedVariant = resolveEducationDesignVariant(variant, allowedDesignVariants);
  const hasConfirmedActiveAssignment = Boolean(
    target && !isLoading && isExperimentActive && assignedVariant,
  );
  const resolvedVariant =
    (target && previewOverride) ||
    (hasConfirmedActiveAssignment ? assignedVariant : null) ||
    'control';
  const isControlRoomDesign = isEducationWorkbenchVariant(resolvedVariant);
  const isClarityDesign = resolvedVariant === 'clarity';
  const skipTarget = location.pathname.startsWith('/courses/')
    ? '#course-content'
    : location.pathname.replace(/\/+$/, '') === '/challenges'
      ? '#challenge-top'
      : null;

  useEffect(() => {
    if (target && !previewOverride && hasConfirmedActiveAssignment && assignedVariant) {
      writeSessionAssignment(target.sessionKey, assignedVariant);
    }
  }, [assignedVariant, hasConfirmedActiveAssignment, previewOverride, target]);

  useLayoutEffect(() => {
    if (!isControlRoomDesign || !location.hash) return undefined;

    let targetId = '';
    try {
      targetId = decodeURIComponent(location.hash.slice(1));
    } catch {
      return undefined;
    }
    if (!targetId) return undefined;

    const scrollToTarget = (): boolean => {
      const target = document.getElementById(targetId);
      if (!target) return false;
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
      return true;
    };

    if (scrollToTarget()) return undefined;

    const observer = new MutationObserver(() => {
      if (scrollToTarget()) observer.disconnect();
    });
    observer.observe(rootRef.current || document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isControlRoomDesign, location.hash, location.pathname]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !isControlRoomDesign) return undefined;

    const sectionSelector = [
      '.course-page--workbench .cpw-main > .crx-section',
      '.course-page--workbench .cpw-main > .crx-control-proof',
      '.course-page--workbench .cpw-main > .crx-freeband',
      '.course-page--workbench .cpw-main > .crx-final',
      '.challenge-page--workbench > .chp-program',
      '.challenge-page--workbench > .chp-preparation',
      '.challenge-page--workbench > .chp-logistics-section',
      '.challenge-page--workbench > .cp-faq',
      '.challenge-page--workbench > .crx-closing',
    ].join(', ');
    const prefersReducedMotion = Boolean(
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches,
    );
    const observed = new Set<HTMLElement>();
    let revealFrame = 0;
    const reveal = (element: HTMLElement): void => {
      element.dataset.eduReveal = 'visible';
    };
    const supportsIntersectionObserver = 'IntersectionObserver' in window;
    const intersectionObserver =
      !prefersReducedMotion && supportsIntersectionObserver
        ? new window.IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!(entry.target instanceof HTMLElement)) return;
                // A fast scroll can jump a short section from below the viewport
                // to above it without an intersecting frame. Reveal passed
                // sections too so motion never strands real content.
                if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
                const element = entry.target;
                reveal(element);
                intersectionObserver?.unobserve(element);
              });
            },
            { threshold: 0.08, rootMargin: '0px 0px -8% 0px' },
          )
        : null;

    const revealPassedSections = (): void => {
      revealFrame = 0;
      observed.forEach((element) => {
        if (element.dataset.eduReveal !== 'pending') return;
        if (element.getBoundingClientRect().top >= window.innerHeight * 0.92) return;
        reveal(element);
        intersectionObserver?.unobserve(element);
      });
    };

    const handleScroll = (): void => {
      if (revealFrame) return;
      revealFrame = window.requestAnimationFrame(revealPassedSections);
    };

    const registerSections = (): void => {
      root.querySelectorAll<HTMLElement>(sectionSelector).forEach((element) => {
        if (observed.has(element)) return;
        observed.add(element);

        const alreadyInView = element.getBoundingClientRect().top < window.innerHeight * 0.92;
        if (!intersectionObserver || alreadyInView) {
          reveal(element);
          return;
        }

        element.dataset.eduReveal = 'pending';
        intersectionObserver.observe(element);
      });
    };

    registerSections();
    const mutationObserver = new MutationObserver(registerSections);
    mutationObserver.observe(root, { childList: true, subtree: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mutationObserver.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (revealFrame) window.cancelAnimationFrame(revealFrame);
      observed.forEach((element) => delete element.dataset.eduReveal);
    };
  }, [isControlRoomDesign, location.pathname]);

  return (
    <EducationDesignContext.Provider value={resolvedVariant}>
      <div
        ref={rootRef}
        className={[
          'education-design-experiment',
          isControlRoomDesign ? 'education-design-experiment--control-room' : '',
          isClarityDesign ? 'education-design-experiment--clarity' : '',
          isClarityDesign && target?.surface === 'ai_trading_bootcamp_page'
            ? 'course-clarity-review'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="education-design-experiment"
        data-education-design-variant={resolvedVariant}
        data-education-theme={resolvedTheme}
        data-education-theme-source="preference"
        data-experiment-active={String(hasConfirmedActiveAssignment && !previewOverride)}
        data-experiment-name={target?.experiment.name || 'none'}
        data-experiment-target={String(isTargetPath)}
      >
        {isControlRoomDesign && skipTarget ? (
          <a className="edu-skip-link" href={skipTarget}>
            Skip to content
          </a>
        ) : null}
        {children}
        {isClarityDesign ? (
          <ChallengeClarityPaint rootSelector='[data-education-design-variant="clarity"]' />
        ) : null}
      </div>
    </EducationDesignContext.Provider>
  );
}
